import { request } from '@reflex/ui/client/http';
import type {
  CreateMarketingPagePayload,
  MarketingDiffSummary,
  MarketingPage,
  MarketingPageVersion,
  MarketingPageVersionSummary,
  PublishedPage,
  PublishedPageSummary,
  PublishPayload,
  SaveVersionPayload,
  UpdateMarketingPagePayload,
} from '../shared/blocks';

const ADMIN = '/marketing-pages';
const PUBLIC = '/public/marketing-pages';

export const marketingApi = {
  listPublished: () => request<PublishedPageSummary[]>(PUBLIC),
  getPublished: (slug: string) => request<PublishedPage>(`${PUBLIC}/${encodeURIComponent(slug)}`),

  list: () => request<MarketingPage[]>(ADMIN),
  get: (id: string) => request<MarketingPage>(`${ADMIN}/${encodeURIComponent(id)}`),
  create: (body: CreateMarketingPagePayload) =>
    request<MarketingPage>(ADMIN, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: UpdateMarketingPagePayload) =>
    request<MarketingPage>(`${ADMIN}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    request<{ ok: true }>(`${ADMIN}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  publish: (id: string, body: PublishPayload = {}) =>
    request<MarketingPage>(`${ADMIN}/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  unpublish: (id: string) =>
    request<MarketingPage>(`${ADMIN}/${encodeURIComponent(id)}/unpublish`, { method: 'POST' }),
  getDiff: (id: string) => request<MarketingDiffSummary>(`${ADMIN}/${encodeURIComponent(id)}/diff`),
  discardDraft: (id: string) =>
    request<MarketingPage>(`${ADMIN}/${encodeURIComponent(id)}/discard-draft`, {
      method: 'POST',
    }),

  // --- Version history ---

  listVersions: (id: string) =>
    request<MarketingPageVersionSummary[]>(`${ADMIN}/${encodeURIComponent(id)}/versions`),
  saveVersion: (id: string, body: SaveVersionPayload = {}) =>
    request<MarketingPageVersion>(`${ADMIN}/${encodeURIComponent(id)}/versions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getVersion: (id: string, versionId: string) =>
    request<MarketingPageVersion>(
      `${ADMIN}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
    ),
  publishVersion: (id: string, versionId: string) =>
    request<MarketingPage>(
      `${ADMIN}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/publish`,
      { method: 'POST' },
    ),
  restoreVersion: (id: string, versionId: string, body: { force?: boolean } = {}) =>
    request<MarketingPage>(
      `${ADMIN}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

export const marketingQueryKeys = {
  all: ['marketing-pages'] as const,
  published: () => ['marketing-pages', 'published'] as const,
  publishedBySlug: (slug: string) => ['marketing-pages', 'published', slug] as const,
  admin: () => ['marketing-pages', 'admin'] as const,
  adminById: (id: string) => ['marketing-pages', 'admin', id] as const,
  versions: (id: string) => ['marketing-pages', 'admin', id, 'versions'] as const,
  version: (id: string, versionId: string) =>
    ['marketing-pages', 'admin', id, 'versions', versionId] as const,
  diff: (id: string) => ['marketing-pages', 'admin', id, 'diff'] as const,
};
